import { ActionTree } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { AxiosResponse } from 'axios'
import {
  AssignUserRoleRequest,
  CancelInvitationRequest,
  UserCredentials,
  UserContact,
  CompanyUser,
} from '@/models/users'
import usersApiService from '@/services/users'
import { RootState } from '../../state'
import { UserState } from './state'
import { UserMutationTypes } from './mutations'
import {
  ActiveScriptMutationTypes,
  ActiveScriptStartMutationPayload,
} from '@/store/modules/active-script/mutations'
import { setCarrotQuestUserParams } from '@/helpers/analytics'
import i18n, { setCurrentLocale } from '@/i18n'

export enum UserActionTypes {
  CancelInvitation = '[User Action] Cancel Invitation', // USERS__CANCEL_INVITATION
  GetCurrentUser = '[User Action] Get Current User', // USERS__ME_GET
  UpdateCurrentUser = '[User Action] Update Current User', // USERS__ME_UPDATE
  UpdateCurrentUserLanguage = '[User Action] Update Current User Language',
  ChangePassword = '[User Action] Change Password', // USERS__ME_CHANGE_PASSWORD
  FetchCompanyUsers = '[User Action] Fetch Company Users', // USERS__FETCH
  FetchInvitations = '[User Action] Fetch Invitations', // USERS__FETCH_INVITATIONS
  FetchRoles = '[User Action] Fetch Roles', // USERS__FETCH_ROLES
  Delete = '[User Action] Delete', // USERS__DELETE
  AssignRole = '[User Action] Assign Role', // USERS__ASSIGN_ROLE
  Invite = '[User Action] Invite', // USERS__INVITE
}

export const actions: ActionTree<UserState, RootState> = {
  /**
   * Assign user a new role
   *
   * @param {UserContext} context
   * @param {AssignUserRoleRequest} payload
   * @returns {AxiosPromise<boolean>}
   */
  async [UserActionTypes.AssignRole](context, payload: AssignUserRoleRequest) {
    const response = await usersApiService.assignRole(payload)
    context.dispatch(UserActionTypes.FetchCompanyUsers)

    return response
  },

  /**
   * Send request to cancel invitation to the active company
   *
   * @param {UserContext} context
   * @param {CancelInvitationRequest} payload
   * @returns {AxiosPromise<boolean>}
   */
  async [UserActionTypes.CancelInvitation](
    context,
    payload: CancelInvitationRequest,
  ) {
    const company = context.state.me.company
    if (company === undefined) throw 'Expected user company to be defined'

    const response = await usersApiService.cancelInvitation(payload, company)
    context.dispatch(UserActionTypes.FetchInvitations)

    return response
  },

  /**
   * Invite user to the active company
   *
   * @param {UserContext} context
   * @param {InvitationRequest} payload
   * @returns {AxiosPromise<boolean>}
   */
  async [UserActionTypes.Invite](context, payload) {
    const response = await usersApiService.invite(payload)

    if (response.data) {
      context.dispatch(ADD_TOAST_MESSAGE, {
        text: 'Приглашение отправлено',
        type: 'info',
      })
      context.dispatch(UserActionTypes.FetchCompanyUsers)
      context.dispatch(UserActionTypes.FetchInvitations)
    }

    return response
  },

  /**
   * Fetch data about my user
   *
   * @param {UserContext} context
   * @returns {AxiosPromise<UserFull>}
   */
  async [UserActionTypes.GetCurrentUser](context) {
    const response = await usersApiService.getCurrentUser()

    context.commit(UserMutationTypes.SetCurrentUser, response.data)

    if (
      typeof response.data.company === 'string' &&
      response.data.company !== '0'
    ) {
      context.commit(UserMutationTypes.SetActiveCompany, response.data.company)
    }

    if (typeof response.data.script === 'string') {
      const startMutationPayload: ActiveScriptStartMutationPayload = {
        scriptId: response.data.script,
      }
      context.commit(ActiveScriptMutationTypes.Start, startMutationPayload)
    }

    setCurrentLocale(response.data.language)
    setCarrotQuestUserParams(response.data)
  },

  /**
   * Update user information
   *
   * @param {UserContext} context
   * @param {UserContact} payload
   * @returns {AxiosPromise<boolean>}
   */
  async [UserActionTypes.UpdateCurrentUser](context, payload: UserContact) {
    const response = await usersApiService.updateCurrentUser(payload)

    if (response.data) {
      context.dispatch(ADD_TOAST_MESSAGE, {
        text: i18n.tc('user.actions.updateSuccess'),
        type: 'info',
      })
      context.dispatch(UserActionTypes.GetCurrentUser)
    }
    return response
  },

  async [UserActionTypes.UpdateCurrentUserLanguage](context, payload: string) {
    const isLoggedIn = context.rootGetters.isLoggedIn
    return isLoggedIn
      ? usersApiService.updateCurrentUserLanguage(payload)
      : Promise.resolve()
  },

  async [UserActionTypes.ChangePassword](context, payload: UserCredentials) {
    return usersApiService.changePassword(payload)
  },

  /**
   * Delete user from company
   *
   * @param {UserContext} context
   * @param {string} id
   * @returns {AxiosPromise<boolean>}
   */
  async [UserActionTypes.Delete](context, id: string) {
    const response = await usersApiService.delete(id)
    context.dispatch(UserActionTypes.FetchCompanyUsers)
    return response
  },

  /**
   * Fetch company's users list
   *
   * @param {UserContext} context
   * @returns {AxiosPromise<CompanyUser[]>}
   */
  async [UserActionTypes.FetchCompanyUsers](
    context,
  ): Promise<AxiosResponse<CompanyUser[]>> {
    const companyId = context.state.me.company
    if (companyId === undefined) throw 'Expected companyId to be defined'
    const response = await usersApiService.getCompanyUsers(companyId)

    context.commit(UserMutationTypes.Save, response.data)
    return response
  },

  /**
   * Fetch company's invitations list
   *
   * @param {UserContext} context
   * @returns {AxiosPromise<UserInvitation[]>}
   */
  async [UserActionTypes.FetchInvitations](context) {
    const companyId = context.state.me.company
    if (companyId === undefined) throw 'Expected companyId to be defined'
    const response = await usersApiService.getCompanyInvitations(companyId)

    context.commit(UserMutationTypes.SaveInvitations, response.data)
    return response
  },

  /**
   * Fetch company's roles list
   *
   * @param {UserContext} context
   * @returns {AxiosPromise<CompanyRole[]>}
   */
  async [UserActionTypes.FetchRoles](context) {
    const companyId = context.state.me.company
    if (companyId === undefined) throw 'Expected companyId to be defined'
    const response = await usersApiService.getCompanyRoles(companyId)
    context.commit(UserMutationTypes.SaveRoles, response.data)
    return response
  },
}
